import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Result, Spin } from 'antd';
import { useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";

const SignUp = lazy(() => import("../Authentication/SignUp"));
const DashboardLayout = lazy(() => import("../layout/DashboardLayout"));
const FileUploadAndProcessForm = lazy(() => import("../components/DataUpload/FileUploadAndProcessForm"));
const DataReviewAndConfigure = lazy(() => import("../components/DataHistory/DataReviewAndConfigure"));
const WelcomeDashboard = lazy(() => import("../components/Dashboard/WelcomeDashboard"));
const Login = lazy(() => import("../Authentication/Login"));
const ForgotPassword = lazy(() => import("../Authentication/ForgotPassword"));
const EmailVerificationPage = lazy(() => import("../Authentication/EmailVerificationPage"));
const LinkedInCallback = lazy(() => import("../Authentication/LinkedInCallback"));
const PhoneVerificationPage = lazy(() => import("../Authentication/PhoneVerificationPage"));
const Help = lazy(() => import("../Authentication/Help"));
const AnalyticsDashboard = lazy(() => import("../components/DataHistory/AnalyticsDashboard"));
const LandingPage = lazy(() => import("../pages/LandingPage"));
const AboutUs = lazy(() => import("../pages/AboutUs"));
const Security = lazy(() => import("../pages/Security"));
const UseCase = lazy(() => import("../pages/UseCase"));
const Pricing = lazy(() => import("../pages/Pricing"));
const ContactUs = lazy(() => import("../pages/ContactUs"));
const TermsOfUse = lazy(() => import("../pages/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const UserHistoryDashboard = lazy(() => import("../components/Dashboard/UserHistoryDashboard"));
const SmartMergeDashboard = lazy(() => import("../components/SmartMerge/SmartMergeDashboard"));
const PdfConverter = lazy(() => import("../components/PdfConverter/PdfConverter"));
const ExpenseClaimDashboard = lazy(() => import("../components/ExpenseClaim/ExpenseClaimDashboard"));
const NotFound = lazy(() => import("../pages/NotFound"));

function PrivateRoute({ children }) {
  const location = useLocation();
  const { isLoggedIn, accessToken, refreshToken, googleAccessToken, linkedin_access_token } = useSelector((state) => state.auth);
  const isAuthorized = Boolean(isLoggedIn && (accessToken || refreshToken|| googleAccessToken || linkedin_access_token));
  const isReactSnap = navigator.userAgent === "ReactSnap";
  
  if (!isAuthorized && !isReactSnap) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
 
  return children;
}

function PublicRoute({ children }) {
  const { isLoggedIn, accessToken, refreshToken, googleAccessToken, linkedin_access_token } = useSelector((state) => state.auth);
  const isAuthorized = Boolean(isLoggedIn && (accessToken || refreshToken || googleAccessToken || linkedin_access_token));
 
  if (isAuthorized) {
    return <Navigate to="/dashboard" replace />;
  }
 
  return children;
}

const AppRoutes = () => {
   const navigate = useNavigate();
   const applyResponse = useSelector((s) => s.file?.applyResponse);

   const GOOGLE_CLIENT_ID =  process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Suspense fallback={<div className="w-full h-screen flex justify-center items-center bg-black"><Spin size="large" /></div>}>
        <Routes>
          {/* Public */}
          <Route path="/linkedin-callback/*" element={<LinkedInCallback />} />
          <Route index element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/use-case" element={<UseCase />} />
          <Route path="/security" element={<Security />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/login/help" element={<Help />} />
          <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/verify-email" element={<PublicRoute><EmailVerificationPage /></PublicRoute>} />
          <Route path="/verify-phone" element={<PublicRoute><PhoneVerificationPage /></PublicRoute>} />

          {/* Protected layout with nested routes */}
          <Route
            path="/dashboard/*"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            {/* index = /dashboard */}
            <Route index element={<WelcomeDashboard />} />
            {/* /dashboard/data-entry */}
            <Route path="data-entry" element={<FileUploadAndProcessForm />} />
            {/* /dashboard/smart-merge */}
            <Route path="smart-merge" element={<SmartMergeDashboard />} />
            {/* /dashboard/anamoly-detection */}
            <Route path="anamoly-detection" element={<DataReviewAndConfigure />} />
            {/* /dashboard/anamoly-detection/analytic-dashboard */}
            <Route path="anamoly-detection/analytic-dashboard" element={<AnalyticsDashboard applyResponse={applyResponse} />} />
            {/* /dashboard/data-history */}
            <Route path="data-history" element={<UserHistoryDashboard />} />
            {/* /dashboard/subscription */}
            <Route path="subscription" element={<Pricing isInDashboard={true} />} />
            {/* /dashboard/use-case */}
            <Route path="use-case" element={<UseCase isInDashboard={true} />} />
            {/* /dashboard/contact-us */}
            <Route path="contact-us" element={<ContactUs isInDashboard={true} />} />
            {/* /dashboard/pdf-converter */}
            {/* <Route path="pdf-converter" element={<PdfConverter />} /> */}
            {/* /dashboard/expense-claim */}
            <Route path="expense-claim" element={<ExpenseClaimDashboard />} />
          </Route>

          {/* 404 Component */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </GoogleOAuthProvider>
  );
};

export default AppRoutes;
